import {
  ButtonType,
  CaesarsButton,
} from '@eCommerce/frontend-legacy-shared-library';
import {faShieldCheck} from '@fortawesome/pro-solid-svg-icons/faShieldCheck';
import {faTimes} from '@fortawesome/pro-solid-svg-icons/faTimes';
import {faTriangleExclamation} from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ReactComponent as Logo} from 'assets/svg/caesars_rewards_dark.svg';
import {ReactComponent as Waves} from 'assets/svg/waves-top-left-gray.svg';
import {Pressable, Text, View} from 'native-base';
import React, {ReactNode} from 'react';

import {useDismissBehavior} from 'hooks/useDismissBehavior';
import {useIsMD} from 'hooks/useIsMD';
import {useIsOpenWebView} from 'hooks/useIsOpenWebView';

import BodyText from './BodyText';
import PanelLoader from './PanelLoader';

export interface PanelContainerProps {
  title?: string;
  subTitle?: string;
  bodyText?: string | ReactNode;
  primaryTitle?: string;
  secondaryTitle?: string;
  textLink?: string;
  primaryDescription?: string;
  onPrimaryPress?: () => void;
  onSecondaryPress?: () => void;
  onTextLinkPress?: () => void;
  onDismiss?: () => void;
  isMD?: boolean;
  legalCopyTitle?: string;
  legalCopyText?: string | ReactNode;
  children?: React.ReactElement;
  isDialogBox?: boolean;
  preserveButtonOrderMobile?: boolean;
  bodyTextBottomMargin?: string;
  isLoading?: boolean;
  closeTrackingEvent?: string;
  hideCloseBtn?: boolean;
  secondaryType?: ButtonType;
  justifySecondaryForMD?: boolean;
}

export const BoldedText = ({
  fontSize,
  text,
  boldText,
  lineHeight,
}: {
  fontSize: string;
  text: string;
  boldText: string;
  lineHeight: string;
}): ReactNode => {
  const textArray = text.split(boldText);
  return textArray.map((item, index) => (
    <Text
      key={`bolded-text-${index}`}
      color="tuna"
      fontSize={fontSize}
      fontFamily="body"
      lineHeight={lineHeight}
    >
      {item}
      {index !== textArray.length - 1 && (
        <Text
          color="tuna"
          fontSize={fontSize}
          fontFamily="body"
          fontWeight="bold"
          lineHeight={lineHeight}
        >
          {boldText}
        </Text>
      )}
    </Text>
  ));
};

const PanelContainer: React.FunctionComponent<PanelContainerProps> = (
  props,
) => {
  const {
    title,
    subTitle,
    bodyText,
    primaryTitle,
    secondaryTitle,
    onPrimaryPress,
    onSecondaryPress,
    legalCopyText,
    legalCopyTitle,
    children,
    onDismiss,
    textLink,
    onTextLinkPress,
    isDialogBox,
    preserveButtonOrderMobile = false,
    bodyTextBottomMargin = '16px',
    isLoading,
    closeTrackingEvent = 'mfa close',
    hideCloseBtn = false,
    secondaryType = undefined,
    justifySecondaryForMD = false,
  } = props;
  const standardDismissBehavior = useDismissBehavior(closeTrackingEvent);
  const isMD = useIsMD();
  const disableEvent = React.useCallback(
    (e: React.SyntheticEvent) => {
      e.persist();

      if (isLoading) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [isLoading],
  );

  // Hide close button if the prop is provided or if isopenwebview=true
  const isOpenWebView = useIsOpenWebView();
  const shouldHideCloseBtn = hideCloseBtn || isOpenWebView;

  return React.createElement(
    'div',
    {
      onClick: disableEvent,
      onKeyDown: disableEvent,
      ...(isLoading && {
        tabIndex: -1,
        'aria-hidden': 'true',
      }),
    },
    <View
      flex={1}
      alignItems="center"
      justifyContent={isMD ? 'center' : undefined}
      minH={isMD ? '100vh' : undefined}
      w={isMD ? '100%' : undefined}
      bgColor="#F7F7F8"
      style={{
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: 4,
        },
        shadowOpacity: 0.1,
        shadowRadius: 6.0,
      }}
    >
      {isMD && !shouldHideCloseBtn && (
        <Pressable
          position="absolute"
          onPress={onDismiss ?? standardDismissBehavior}
          alignSelf="flex-end"
          p="24px"
          top={0}
          right={0}
        >
          <FontAwesomeIcon
            icon={faTimes}
            color="#343840"
            style={{fontSize: 30}}
          />
        </Pressable>
      )}
      <View
        style={{
          shadowColor: '#000',
          shadowOffset: {
            width: 0,
            height: 4,
          },
          shadowOpacity: 0.1,
          shadowRadius: 6.0,
        }}
        bgColor="white"
        p={isMD ? '64px' : '16px'}
        justifyContent="space-between"
        h={isMD ? undefined : '100vh'}
        maxWidth={isMD ? '600px' : '100%'}
        minH="375px"
        width="100%"
        minWidth={isMD ? '600px' : '375px'}
      >
        {isLoading && <PanelLoader />}
        <Waves style={{position: 'absolute', top: 0, left: 0}} />
        <View>
          {!isMD && !shouldHideCloseBtn && (
            <Pressable
              onPress={onDismiss ?? standardDismissBehavior}
              alignSelf="flex-end"
            >
              <FontAwesomeIcon
                icon={faTimes}
                color="#343840"
                style={{fontSize: 24}}
              />
            </Pressable>
          )}
          {!isDialogBox && (
            <View
              mt={isMD ? undefined : '32px'}
              mb={isMD ? '48px' : '32px'}
              alignItems="center"
            >
              <Logo width="108px" height="38px" />
            </View>
          )}
          {title && (
            <View mb="16px" flexDirection="row">
              <FontAwesomeIcon
                icon={isDialogBox ? faTriangleExclamation : faShieldCheck}
                color="#A41B32"
                style={{fontSize: 22, marginRight: '8px'}}
              />
              <Text
                textTransform="uppercase"
                color="#343840"
                fontFamily="heading"
                fontWeight="bold"
                fontSize={isMD ? '22px' : '18px'}
              >
                {title}
              </Text>
            </View>
          )}
          {subTitle && (
            <Text
              color="tuna"
              fontFamily="body"
              mb="16px" // should this be conditional based on if bodyText exists?
              fontWeight="bold"
              fontSize="14px"
            >
              {subTitle}
            </Text>
          )}
          {bodyText && (
            <Text
              lineHeight="24px"
              color="tuna"
              mb={bodyTextBottomMargin}
              fontFamily="body"
            >
              {bodyText}
            </Text>
          )}
          {children}
          <View mb={isMD ? '32px' : '16px'}>
            {(legalCopyTitle || legalCopyText) && (
              <View marginTop="16px">
                <BodyText fontSize="12px" bold>
                  {legalCopyTitle && (
                    <BodyText fontSize="12px" bold>
                      {legalCopyTitle}
                      {'  '}
                    </BodyText>
                  )}
                  {legalCopyText && (
                    <BodyText fontSize="12px">{legalCopyText}</BodyText>
                  )}
                </BodyText>
              </View>
            )}
          </View>
        </View>
        <View
          mx={isMD ? undefined : '-16px'}
          px={isMD ? undefined : '16px'}
          pt={isMD ? undefined : '16px'}
          justifyContent={
            isMD
              ? textLink || justifySecondaryForMD
                ? 'space-between'
                : 'flex-end'
              : 'center'
          }
          flexDirection={
            isMD
              ? 'row'
              : preserveButtonOrderMobile
              ? 'column'
              : 'column-reverse'
          }
          style={
            isMD
              ? null
              : {
                  shadowColor: '#000',
                  shadowOffset: {
                    width: 0,
                    height: -10,
                  },
                  shadowOpacity: 0.1,
                  shadowRadius: 25,
                }
          }
        >
          {textLink && onTextLinkPress !== undefined && (
            <Pressable
              onPress={onTextLinkPress}
              h="36px"
              alignItems="center"
              justifyContent="center"
              _hover={{
                textDecoration: 'underline',
              }}
            >
              <Text
                fontSize="12px"
                fontFamily="body"
                fontWeight="semibold"
                color="tuna"
              >
                {textLink}
              </Text>
            </Pressable>
          )}
          {textLink && onTextLinkPress === undefined && (
            <Text
              fontSize="12px"
              fontFamily="body"
              fontWeight="semibold"
              color="#343840"
              marginTop="8px"
              alignItems="center"
              justifyContent="center"
            >
              {textLink}
            </Text>
          )}
          {secondaryTitle && !textLink && (
            <CaesarsButton
              isFullWidth={!isMD}
              title={secondaryTitle}
              onPress={onSecondaryPress}
              type={secondaryType ?? ButtonType.TEXT_CTA}
            />
          )}
          <View h={isMD ? 0 : '8px'} w={isMD ? '8px' : 0} />
          {primaryTitle && (
            <CaesarsButton
              isFullWidth={!isMD}
              title={primaryTitle}
              onPress={onPrimaryPress}
            />
          )}
        </View>
      </View>
    </View>,
  );
};

export default PanelContainer;
